<template>
  <div class="brand">
    <breadcrumbs :breadcrumbsData="breadcrumbsData" #icon>
      <svg class="icon" aria-hidden="true">
        <use xlink:href="#icon-pinpai"></use>
      </svg>
    </breadcrumbs>
    <div class="table">
      <div class="table-head">
        <a-space style="margin-top: 16px;">
          <ButtonGroups :pagination="pagination" :tableData="dataSource" @pageChange="onPageChange" />
          <a-input-search v-model:value="keywords" :placeholder="$t('seller.common.searchPlaceholder')" allow-clear
            @search="onSearch" @enter="onSearch">
          </a-input-search>
        </a-space>

        <div class="filters">

          <div class="filter-item" ref="publisherRef">
            <div  class="label">{{ $t('seller.sellerAccount.publisherLabel') }}</div>
            <a-select  style="width: 180px" v-model:value="publisherId"
              :placeholder="$t('seller.common.selectPublisher')" :getPopupContainer="() => $refs.publisherRef"
              :show-search="true" :virtual="true" :allowClear="true" :not-found-content="!loadingPublisher
                  ? $t('seller.partnershipsPage.selectNoData')
                  : $t('seller.partnershipsPage.selectLoading')
                " @change="onStatusChange" :filter-option="false" @search="handleSearchPublisher"
              @popupScroll="handlePopupScrollPublisher">
              <a-select-option v-for="item in publisherList" :key="item.value" :value="item.value">{{ item.label
                }}</a-select-option>
            </a-select>
          </div>
          <div class="filter-item" ref="statusFilterRef">
            <div class="label">{{ $t('seller.common.status') }}</div>
            <artmisads-select v-model:value="status" style="width: 180px"
              :getPopupContainer="() => $refs.statusFilterRef" @change="onStatusChange" trigger="click">
              <a-select-option value="">{{ $t('seller.common.all') }}</a-select-option>
              <a-select-option value="DELIVERING">{{
                $t('seller.sellerAccount.campaignStatusDelivering')
              }}</a-select-option>
              <a-select-option value="ENDED">{{
                $t('seller.sellerAccount.campaignStatusEnded')
              }}</a-select-option>
              <a-select-option value="EXPIRED">{{
                $t('seller.sellerAccount.campaignStatusExpired')
              }}</a-select-option>
              <a-select-option value="OUTOFBUDGET">{{
                $t('seller.sellerAccount.campaignStatusOutOfBudget')
              }}</a-select-option>
              <a-select-option value="CREATED">{{
                $t('seller.sellerAccount.campaignStatusCreated')
              }}</a-select-option>
              <a-select-option value="DELETED">{{
                $t('seller.sellerAccount.campaignStatusDeleted')
              }}</a-select-option>
            </artmisads-select>
          </div>
          <div class="filter-item" ref="operationRef">
            <div class="label" >{{
              $t('seller.sellerAccount.sortLabel')
            }}</div>
            <artmisads-select v-model:value="sortBy" style="width: 180px" @change="onSortChange"
              :getPopupContainer="() => $refs.operationRef">
              <a-select-option value="startDateAsc">
                <Icon name="paixu1" class="iconAsc paixuIcon" />
                <span class="paixuText">{{
                  $t('seller.sellerAccount.sortOptionStartDate')
                }}</span>
              </a-select-option>
              <a-select-option value="startDateDesc">
                <Icon name="paixu1" />
                <span class="paixuText">{{
                  $t('seller.sellerAccount.sortOptionStartDate')
                }}</span>
              </a-select-option>

              <a-select-option value="endDateAsc">
                <Icon name="paixu1" class="iconAsc paixuIcon" />
                <span class="paixuText">{{
                  $t('seller.sellerAccount.sortOptionEndDate')
                }}</span>
              </a-select-option>
              <a-select-option value="endDateDesc" class="paixuIcon">
                <Icon name="paixu1" />
                <span class="paixuText">{{
                  $t('seller.sellerAccount.sortOptionEndDate')
                }}</span>
              </a-select-option>
              <a-select-option value="budgetAsc">
                <Icon name="paixu1" class="iconAsc paixuIcon" />
                <span class="paixuText">{{
                  $t('seller.sellerAccount.sortOptionBudget')
                }}</span>
              </a-select-option>
              <a-select-option value="budgetDesc" class="paixuIcon">
                <Icon name="paixu1" />
                <span class="paixuText">{{
                  $t('seller.sellerAccount.sortOptionBudget')
                }}</span>
              </a-select-option>
              <a-select-option value="commissionAsc">
                <Icon name="paixu1" class="iconAsc paixuIcon" />
                <span class="paixuText">{{
                  $t('seller.sellerAccount.sortOptionCommission')
                }}</span>
              </a-select-option>
              <a-select-option value="commissionDesc" class="paixuIcon">
                <Icon name="paixu1" />
                <span class="paixuText">{{
                  $t('seller.sellerAccount.sortOptionCommission')
                }}</span>
              </a-select-option>
            </artmisads-select>
          </div>
        </div>
      </div>
      <artmisads-table v-model:pagination="pagination" :columns="columns" :data-source="dataSource"
        :change="onTableChange" :loading="load" :scroll="{ x: 1500 }">
        <template #bodyCell="{ column, record }">
          <template v-if="column.key === 'dates'">
            <div>
              {{ $t('seller.partnershipsPage.collaborationStart') }}
              {{
                moment(record.startDateTimeInEpochMills)
                  .tz(timezone)
                  .format('YYYY-MM-DD')
              }}
            </div>
            <div>
              {{ $t('seller.partnershipsPage.collaborationEnd') }}
              {{
                moment(record.scheduledEndDateTimeInEpochMills)
                  .tz(timezone)
                  .format('YYYY-MM-DD')
              }}
            </div>
          </template>
          <template v-if="column.key === 'campaignStatus'">
            <Status :noBorder="true" :type="returnStatusType(record.campaignStatus)" :text="campaignStatusText(record.campaignStatus)" />
          </template>
          <template v-if="column.key == 'totalBudget'">
            {{ CURRENCY_DATA_MAPPER[record.currencyCode] }}
            {{ record.totalBudget }}
          </template>
          <template v-if="column.key == 'productsCount'">
            <a class="linkStyle" href="#" @click="showProductsDrawer(record)">{{
              record.productsCount
            }}</a>
          </template>
          <template v-if="column.key == 'publishersCount'">
            <a class="linkStyle" href="#" @click="showPublishersDrawer(record)">{{ record.publishersCount }}</a>
          </template>
          <template v-if="column.key == 'bonusProgramTitle'">
            <a class="linkStyle" href="#" @click="showBonusProgramDrawer(record)">{{ record.bonusProgramTitle }}</a>
          </template>
        </template>
      </artmisads-table>
      <campaignsModal ref="campaignsModalRef" />
    </div>
  </div>
</template>
<script setup lang="jsx">
import breadcrumbs from '../breadcrumbs.vue'
import ButtonGroups from '../Brands/components/buttonGroups.vue'
import Icon from '@/components/Icon.vue'
import Status from '~/components/Status.vue'
import moment from 'moment'
import { ref, reactive, onMounted, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { sellerAccountApi } from '~/server'
import { COUNTRY_TIME_TZ } from '~/contants/common.js'
import { useUserInfoStore } from '@/storeData'
import { CURRENCY_DATA_MAPPER } from '@/contants/common'
import campaignsModal from './components/campaignsModal.vue'
import { message } from 'ant-design-vue'
import { publishersApi } from '@/server'
import { useRoute } from 'vue-router'
import { useRouter } from 'vue-router'
const route = useRoute()
const router = useRouter()
const { t } = useI18n()
const user = useUserInfoStore()
const timezone = COUNTRY_TIME_TZ[user.userInfo.countryCode]
const load = ref(false)
const keywords = ref('')
const status = ref('')
const sortBy = ref('startDateDesc')
const campaignsModalRef = ref(null)

const breadcrumbsData = computed(() => ({
  title: t('seller.menu.campaigns'),
  des: t('seller.sellerAccount.pageDesCampaigns'),
  hasTitleIcon: false,
  links: [
    { name: t('seller.common.home'), path: '/seller/dashboard' },
    {
      name: t('seller.menu.creatorConnections'),
      path: '/seller/creatorConnections/accBonusProgram',
    },
    {
      name: t('seller.menu.campaigns'),
      path: '/seller/creatorConnections/campaigns',
    },
  ],
}))

const columns = computed(() => [
  {
    title: t('seller.sellerAccount.colCampaignName'),
    dataIndex: 'campaignName',
    key: 'campaignName',
  },
  {
    title: t('seller.common.brand'),
    dataIndex: 'brandName',
    key: 'brandName',
  },
  {
    title: t('seller.sellerAccount.colStatus'),
    dataIndex: 'campaignStatus',
    key: 'campaignStatus',
  },
  {
    title: t('seller.sellerAccount.colDates'),
    dataIndex: 'dates',
    key: 'dates',
    width: 200,
  },
  {
    title: t('seller.sellerAccount.colBudget'),
    dataIndex: 'totalBudget',
    key: 'totalBudget',
    width: 120,
  },
  {
    title: t('seller.sellerAccount.colCommission'),
    dataIndex: 'commissionPercentage',
    key: 'commissionPercentage',
    customRender: ({ text, record }) => {
      return `${record.commissionPercentage}%`
    },
  },
  {
    title: t('seller.sellerAccount.colProducts'),
    dataIndex: 'productsCount',
    key: 'productsCount',
  },
  {
    title: t('seller.sellerAccount.colPublishers'),
    dataIndex: 'publishersCount',
    key: 'publishersCount',
  },
  {
    title: t('seller.sellerAccount.colBonusProgram'),
    dataIndex: 'bonusProgramTitle',
    key: 'bonusProgramTitle',
  }
])
const pagination = reactive({
  total: 0,
  defaultPageSize: 10,
  size: 10,
  current: 1,
  showSizeChanger: true,
  pageSize: 10,
  pageSizeOptions: ['10', '20', '30', '40', '50'],
})
const onTableChange = ({ current, pageSize }) => {
  pagination.current = current
  pagination.size = pageSize
  pagination.pageSize = pageSize
  getCampaignsList()
}
const campaignStatusText = (status) => {
  const map = {
    DELIVERING: 'seller.sellerAccount.campaignStatusDelivering',
    ENDED: 'seller.sellerAccount.campaignStatusEnded',
    EXPIRED: 'seller.sellerAccount.campaignStatusExpired',
    OUTOFBUDGET: 'seller.sellerAccount.campaignStatusOutOfBudget',
    CREATED: 'seller.sellerAccount.campaignStatusCreated',
    DELETED: 'seller.sellerAccount.campaignStatusDeleted',
  }
  const key = map[status]
  return key ? t(key) : status
}
const onPageChange = (current) => {
  pagination.current = current
  getCampaignsList()
}
const dataSource = ref([])
const getCampaignsList = async () => {
  load.value = true

  const param = {
    page: pagination.current,
    size: pagination.size,
    keyword: keywords.value,
    campaignStatus: status.value,
    publisherId: publisherId.value,
  }
  if (sortBy.value) {
    if (sortBy.value === 'startDateAsc') {
      param.sortField = 'startDate'
      param.sortDirection = 'asc'
    }
    if (sortBy.value === 'startDateDesc') {
      param.sortField = 'startDate'
      param.sortDirection = 'desc'
    }
    if (sortBy.value === 'endDateAsc') {
      param.sortField = 'endDate'
      param.sortDirection = 'asc'
    }
    if (sortBy.value === 'endDateDesc') {
      param.sortField = 'endDate'
      param.sortDirection = 'desc'
    }
    if (sortBy.value === 'commissionAsc') {
      param.sortField = 'commission'
      param.sortDirection = 'asc'
    }
    if (sortBy.value === 'commissionDesc') {
      param.sortField = 'commission'
      param.sortDirection = 'desc'
    }
    if (sortBy.value === 'budgetAsc') {
      param.sortField = 'budget'
      param.sortDirection = 'asc'
    }
    if (sortBy.value === 'budgetDesc') {
      param.sortField = 'budget'
      param.sortDirection = 'desc'
    }
  }
  const res = await sellerAccountApi.getCampaignsList(param)
  const { data, success, error } = res
  if (success) {
    dataSource.value = data.data
    pagination.total = data.total
    load.value = false
  }
}
const onStatusChange = (val) => {
  getCampaignsList()
}
const showProductsDrawer = (record) => {
  campaignsModalRef.value.openModal('products', record.campaignId)
}
const showPublishersDrawer = (record) => {
  campaignsModalRef.value.openModal('publishers', record.campaignId)
}
const showBonusProgramDrawer = (record) => {
  router.push(`/seller/creatorConnections/accBonusProgram/view/${record.bonusProgramId}`)
}
const onSortChange = (val) => {
  sortBy.value = val
  pagination.current = 1
  getCampaignsList()
}
const onSearch = () => {
  pagination.current = 1
  getCampaignsList()
}
const returnStatusType = (type) => {
  if (type == 'CREATED' || type == 'EXPIRED') {
    return 'inactive'
  } else if (type == 'DELIVERING') {
    return 'delivering'
  } else if (type == 'ENDED') {
    return 'active'
  } else if (type == 'OUTOFBUDGET') {
    return 'pending'
  } else if (type == 'DELETED') {
    return 'not_authorized'
  }
}
const searchValuePublisher = ref('')
const publisherList = ref([])
const publisherId = ref('')
const hasMorePublisher = ref(true)
const loadingPublisher = ref(false)
const paginationPublisher = reactive({
  size: 10,
  current: 1,
})
const getPublisherList = async (page, searchText, isInit) => {
  // 如果已经没有更多数据或已经在加载中，则不再请求
  if (!hasMorePublisher.value || loadingPublisher.value) return
  loadingPublisher.value = true
  try {
    let data = await publishersApi.getPublishers({
      page: paginationPublisher.current,
      size: paginationPublisher.size,
      associateEnable: '1',
      keyword: searchText, // 添加搜索关键词
    })

    if (data.success) {
      const newPublisherList = data.data.data.map((item) => ({
        label: item.publisherVO?.creatorName,
        value: item.publisherVO?.id,
      }))

      // 如果是第一页，则替换列表，否则追加
      if (page === 1) {
        publisherList.value = newPublisherList
      } else {
        publisherList.value = [...publisherList.value, ...newPublisherList]
      }
      if (isInit) {
        publisherId.value = publisherList.value.find(
          (item) =>
            item.label === sessionStorage.getItem('publiserName') || searchText,
        )?.value
      }
      // 判断是否还有更多数据
      hasMorePublisher.value = newPublisherList.length !== data.data.total
      paginationPublisher.current = page
    } else {
      if (page === 1) {
        publisherList.value = []
      }
    }
  } catch (error) {
    message.error(t('seller.msg.fetchPublisherListFailed'))
  } finally {
    loadingPublisher.value = false
  }
}
const handleSearchPublisher = (value, isInit = false) => {
  searchValuePublisher.value = value
  paginationPublisher.current = 1
  publisherList.value = []
  hasMorePublisher.value = true
  getPublisherList(1, value, isInit)
}
const handlePopupScrollPublisher = (e) => {
  const { target } = e
  const scrollHeight = target.scrollHeight
  const scrollTop = target.scrollTop
  const clientHeight = target.clientHeight

  // 当滚动到底部附近时，加载下一页
  if (
    scrollHeight - scrollTop - clientHeight < 100 &&
    hasMorePublisher.value &&
    !loadingPublisher.value
  ) {
    getPublisherList(
      paginationPublisher.current + 1,
      searchValuePublisher.value,
    )
  }
}
onMounted(async () => {
  if (route.query.publisherId) {
    searchValuePublisher.value = route.query.publisherName
    await getPublisherList(1, route.query.publisherName, true)
  } else {
    await getPublisherList(1, searchValuePublisher.value)
  }
  getCampaignsList()
})
</script>
<style lang="less">
.iconAsc {
  transform: rotate(180deg);
}

.brand {
  padding: 16px;
  padding-top: unset;
}

.linkStyle {
  text-decoration: underline;
}

.table {
  padding: 0 16px;
  // margin: 0 16px;
  background-color: var(--color-white);
  border-radius: 8px;
  box-shadow: var(--section-box-shadow-6);
  // border: 2px solid #E8E5ED;
}

.filters {
  display: flex;
  gap: 22px;
  padding: 15px 16px;
  margin: 16px 0;

  border-radius: 8px;
  background: #F8F7FC;

  .filter-item {
    position: relative;
    .label {

      color: #0f0629;
      font-family: "Sora";
      font-size: 14px;
      font-style: normal;
      font-weight: 500;
      margin-bottom: 6px;
      opacity: 0.9;
    }
  }

  .checkbox {
    display: flex;
    height: 32px;
    padding: 0 8px;
    align-items: center;
    gap: 10px;
    border-radius: 8px;
    background: #EAE8F0;

  }

  .statusFilter {
    width: 135px;
    // position: relative;
    z-index: 2;
    // margin-left: 18px;
    // opacity: 0.7;
    font-family: Sora, sans-serif;
    font-weight: 400;
    font-size: 14px;
    color: var(--dark-2);
    letter-spacing: 0.4px;
    line-height: 20px;
  }
}

.table-head {
  // display: flex;
  // height: 64px;
  // justify-content: space-between;
  // align-items: center;

  // .filters {
  //   display: flex;
  //   justify-content: space-between;

  //   .statusFilter {
  //     position: relative;
  //     z-index: 2;
  //     margin-left: 18px;
  //     // opacity: 0.7;
  //     font-family: Sora, sans-serif;
  //     font-weight: 400;
  //     font-size: 14px;
  //     color: var(--dark-2);
  //     letter-spacing: 0.4px;
  //     line-height: 20px;
  //   }
  // }
}
</style>
